import { useCallback, useEffect, useState } from 'react';
import socketIOClient, { Socket } from 'socket.io-client';
import { debounce } from 'lodash';
import { settings } from '../settings';
import useUserStore from '../stores/user-store';
import usePipelineStore from '../stores/pipeline-store';
import useRuntimeStore from '../stores/runtime-store';
const { diff_match_patch } = require('../diff_match_patch_uncompressed');
const DiffMatchPatch = new diff_match_patch();

type LastUpdate = { lockedUntilTs: number; socketId: string };

const PipelineFetcher: React.FC = ({}) => {
    const { sessionToken } = useUserStore();
    const { setPipeline, pipeline } = usePipelineStore((state: any) => state);
    const { setIsSyncing, setLastEdit, lastEdit, setIsLocked } = useRuntimeStore((state: any) => state);
    const pipelineId = window.location.pathname.split('/')[1];
    const [socket, setSocket] = useState<Socket | null>(null);
    const [lastJsonSent, setLastJsonSent] = useState('{}');
    const [lastJsonRecived, setLastJsonRecived] = useState('{}');
    const [randForLockRefresh, setRandForLockRefresh] = useState(0);

    // - Start socket effect
    useEffect(() => {
        const io = socketIOClient(settings.SOCKETS_URL, {
            auth: {
                resourceId: pipelineId,
                sessionToken: sessionToken,
            },
        });
        setSocket(io);
    }, []);

    useEffect(() => {
        if (new Date().getTime() < lastEdit.lockedUntilTs && socket?.id !== lastEdit.socketId) {
            setIsLocked(true);
            setTimeout(() => {
                setRandForLockRefresh(Math.random());
            }, lastEdit.lockedUntilTs - new Date().getTime());
        } else {
            setIsLocked(false);
        }
    }, [lastEdit, socket, randForLockRefresh]);

    // - Server sent data effects:
    const handleServerSendFullJson = useCallback((data) => {
        setLastEdit(data.lastEdit);
        setLastJsonSent(data.jsonStr);
        setLastJsonRecived(data.jsonStr);

        try {
            if (data.jsonStr !== '{}') {
                setPipeline(JSON.parse(data.jsonStr));
            } else {
                const emptyPipeline: PipelineJson = {
                    editorSettings: {
                        varsOrder: [],
                        stepIds: [],
                        debugInstancesIds: [],
                    },
                    name: 'unnamed pipeline',
                    vars: {},
                    firstStepId: '',
                    steps: {},
                };
                setPipeline(emptyPipeline);
            }
        } catch (err) {
            console.error(`error while parsing pipeline json: ${err}`);
        }
        setIsSyncing(false);
    }, []);
    const handleServerChangedJson = useCallback(
        (data: any) => {
            setLastEdit(data.lastEdit);

            const [recivedJsonStr] = DiffMatchPatch.patch_apply(data.patches, lastJsonRecived);
            // console.log({ recivedJsonStr });
            let recivedPipeline = JSON.parse(recivedJsonStr);
            setLastJsonRecived(recivedJsonStr);
            setPipeline(recivedPipeline);
        },
        [lastJsonRecived, lastEdit, socket, setIsLocked],
    );
    const handleServerSendError = useCallback((data) => {
        if (data?.type === 'invalid_token') {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
            return;
        }
        alert(data?.message);
    }, []);

    useEffect(() => {
        if (!socket) return;
        socket.on('server_changed_json', handleServerChangedJson);
        socket.on('server_send_full_json', handleServerSendFullJson);
        socket.on('server_send_error', handleServerSendError);

        return () => {
            socket.off('server_changed_json', handleServerChangedJson);
            socket.off('server_send_full_json', handleServerSendFullJson);
            socket.off('server_send_error', handleServerSendError);
        };
    }, [socket, lastJsonRecived]);

    // - Lock interface effect:
    // useEffect(() => {
    //     if (!lastEdit || !socket) return;

    //     const { lockedUntilTs, socketId } = lastEdit as any;
    //     if (lockedUntilTs > new Date().getTime() && socket.id !== socketId) {
    //         setRuntimeConfig("isLocked", true);
    //         const timeoutId = setTimeout(() => {
    //             setRuntimeConfig("isLocked", false);
    //         }, lockedUntilTs - new Date().getTime());

    //         return () => {
    //             clearTimeout(timeoutId);
    //         };
    //     } else {
    //         setRuntimeConfig("isLocked", false);
    //     }
    // }, [lastEdit, socket]);

    // - Update server effect
    const updateServer = useCallback(
        debounce((pipeline, lastJsonSent) => {
            if (!socket) return;
            const pipelineStr = JSON.stringify(pipeline);
            if (lastJsonRecived === pipelineStr) {
                // console.log("It's the same...");
                // setRuntimeConfig("isWaitingToSync", false);
                return;
            }
            setLastJsonSent(pipelineStr);

            const patches = DiffMatchPatch.patch_make(lastJsonSent, pipelineStr);

            socket.emit('client_changed_json', { jsonStrDiff: patches });
            setIsSyncing(false);
            // setRuntimeConfig("isWaitingToSync", false);
        }, 1000),
        [socket, lastJsonRecived],
    );
    useEffect(() => {
        // setRuntimeConfig("isWaitingToSync", true);
        updateServer(pipeline, lastJsonSent);
        setIsSyncing(true);
    }, [pipeline, debounce]);

    return null;
};

export default PipelineFetcher;
